/**
 * Type-safe query key factory for TanStack Query
 *
 * Usage:
 *   queryKey: queryKeys.notes.list(userId)
 *   queryKey: queryKeys.notes.detail(noteId)
 *   queryKey: queryKeys.noteLists.detail(listId)
 *
 * Benefits:
 *   - Autocomplete for all query keys
 *   - Type-safe parameters
 *   - Hierarchical invalidation (invalidate all 'notes' queries at once)
 *   - Consistent key structure across the app
 */

export const queryKeys = {
  notes: {
    all: ["notes"] as const,
    lists: () => [...queryKeys.notes.all, "list"] as const,
    list: (userId?: string) => [...queryKeys.notes.lists(), userId] as const,
    home: (userId?: string) =>
      [...queryKeys.notes.all, "home", userId] as const,
    details: () => [...queryKeys.notes.all, "detail"] as const,
    detail: (noteId: string) => [...queryKeys.notes.details(), noteId] as const,
    entities: (noteId: string) =>
      [...queryKeys.notes.all, "entities", noteId] as const,
    comments: (noteId: string) =>
      [...queryKeys.notes.all, "comments", noteId] as const,
    connections: (
      noteId: string,
      options?: {
        types?: string[];
        includeEntityBased?: boolean;
        minSharedEntities?: number;
      },
    ) => [...queryKeys.notes.all, "connections", noteId, options] as const,
    entityConnections: (noteId: string, minSharedEntities?: number) =>
      [
        ...queryKeys.notes.all,
        "entityConnections",
        noteId,
        minSharedEntities,
      ] as const,
    /** @deprecated Use NoteList queries instead. */
    subtasks: (noteId: string) =>
      [...queryKeys.notes.all, "subtasks", noteId] as const,
    checklist: (noteId: string) =>
      [...queryKeys.notes.all, "checklist", noteId] as const,
    activity: (noteId: string) =>
      [...queryKeys.notes.all, "activity", noteId] as const,
  },

  todos: {
    all: ["todos"] as const,
    lists: () => [...queryKeys.todos.all, "list"] as const,
    list: (userId?: string) => [...queryKeys.todos.lists(), userId] as const,
    details: () => [...queryKeys.todos.all, "detail"] as const,
    detail: (todoId: string) => [...queryKeys.todos.details(), todoId] as const,
  },

  noteLists: {
    all: ["noteLists"] as const,
    lists: () => [...queryKeys.noteLists.all, "list"] as const,
    list: (userId?: string) =>
      [...queryKeys.noteLists.lists(), userId] as const,
    details: () => [...queryKeys.noteLists.all, "detail"] as const,
    detail: (listId: string) =>
      [...queryKeys.noteLists.details(), listId] as const,
    noteSmartFillStatus: (listId: string, noteId: string) =>
      [...queryKeys.noteLists.all, "smartFillStatus", listId, noteId] as const,
  },

  entities: {
    all: ["entities"] as const,
    lists: () => [...queryKeys.entities.all, "list"] as const,
    list: (params?: { type?: string; search?: string }) =>
      [...queryKeys.entities.lists(), params] as const,
    details: () => [...queryKeys.entities.all, "detail"] as const,
    detail: (entityId: string) =>
      [...queryKeys.entities.details(), entityId] as const,
    relationships: (entityId: string) =>
      [...queryKeys.entities.all, "relationships", entityId] as const,
  },

  auth: {
    all: ["auth"] as const,
    me: () => [...queryKeys.auth.all, "me"] as const,
    session: () => [...queryKeys.auth.all, "session"] as const,
  },

  search: {
    all: ["search"] as const,
    contextual: (params: {
      keywords?: string[];
      entityTypes?: string[];
      entityNames?: string[];
    }) => [...queryKeys.search.all, "contextual", params] as const,
  },

  unfurl: {
    all: ["unfurl"] as const,
    url: (url: string) => [...queryKeys.unfurl.all, url] as const,
  },

  tags: {
    all: ["tags"] as const,
    list: (userId?: string) => [...queryKeys.tags.all, userId] as const,
  },

  noteTypes: {
    all: ["noteTypes"] as const,
    list: () => [...queryKeys.noteTypes.all, "list"] as const,
  },

  workflow: {
    all: ["workflow"] as const,
    usage: () => [...queryKeys.workflow.all, "usage"] as const,
  },

  checklist: {
    all: ["checklist-items"] as const,
    items: (noteId: string) => [...queryKeys.checklist.all, noteId] as const,
  },

  providers: {
    all: ["providers"] as const,
    list: () => [...queryKeys.providers.all, "list"] as const,
    detail: (slug: string) =>
      [...queryKeys.providers.all, "detail", slug] as const,
    schemaTypes: () => [...queryKeys.providers.all, "schemaTypes"] as const,
    connection: (slug: string) =>
      [...queryKeys.providers.all, "connection", slug] as const,
    connections: () => [...queryKeys.providers.all, "connections"] as const,
  },
} as const;

export type QueryKeys = typeof queryKeys;

export const noteListKeys = queryKeys.noteLists;

export const invalidationPatterns = {
  allNotes: queryKeys.notes.all,
  allTodos: queryKeys.todos.all,
  allNoteLists: queryKeys.noteLists.all,
  allEntities: queryKeys.entities.all,
  allAuth: queryKeys.auth.all,

  noteAndRelated: (noteId: string) => [
    queryKeys.notes.detail(noteId),
    queryKeys.notes.entities(noteId),
    queryKeys.notes.comments(noteId),
    queryKeys.notes.checklist(noteId),
  ],

  noteListAndRelated: (listId: string) => [
    queryKeys.noteLists.detail(listId),
    queryKeys.noteLists.all,
  ],

  afterNoteCreate: [queryKeys.notes.all, queryKeys.todos.all],

  afterNoteUpdate: (noteId: string) => [
    queryKeys.notes.detail(noteId),
    queryKeys.notes.all,
  ],

  afterNoteDelete: [
    queryKeys.notes.all,
    queryKeys.todos.all,
    queryKeys.noteLists.all,
  ],

  afterEntityChange: [queryKeys.entities.all, queryKeys.notes.all],

  afterNoteTypeDetection: [
    queryKeys.notes.all,
    queryKeys.todos.all,
    queryKeys.noteLists.all,
  ],

  afterProviderConnect: (slug: string) => [
    queryKeys.providers.connection(slug),
    queryKeys.providers.connections(),
  ],

  afterProviderDisconnect: (slug: string) => [
    queryKeys.providers.connection(slug),
    queryKeys.providers.connections(),
  ],

  afterProviderSync: (slug: string, listId: string) => [
    queryKeys.providers.connection(slug),
    queryKeys.noteLists.detail(listId),
  ],
};
